import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/ui/sidebar';
import { ClipboardList } from 'lucide-react';
import { useEvaluators } from '../../contexts/EvaluatorContext';
import { Evaluator } from '../../lib/types';
import { useAuthStore } from '../../stores/authStore';
import { useGlobal } from '../../contexts/GlobalProvider';
import { EntityType } from '../../lib/enums';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { ExistingEvaluators } from '../../components/ui/evaluator-builder/existing-evaluators';
import { EvaluatorForm } from '../../components/ui/evaluator-builder/evaluator-form';
import { useTutorialStore } from '../../stores/tutorialStore';

export const EvaluatorBuilder = (): JSX.Element => {
  const { evaluatorId: evaluatorIdFromUrl } = useParams<{
    evaluatorId?: string;
  }>();
  const navigate = useNavigate();
  const { isActive: isOnboardingActive, markMilestoneComplete } =
    useTutorialStore();
  const { user: authUser } = useAuthStore();
  const { hiddenExamples, toggleHiddenExample } = useGlobal();
  const {
    evaluators,
    createEvaluator,
    updateEvaluator,
    deleteEvaluator,
    loading,
    error,
  } = useEvaluators();
  const [editingEvaluator, setEditingEvaluator] = useState<Evaluator | null>(
    null,
  );
  const [evaluatorName, setEvaluatorName] = useState('');
  const [evaluatorStatement, setEvaluatorStatement] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [sectorId, setSectorId] = useState<string | null>(null);

  const isAdmin = authUser?.currentWorkspace?.role?.name === 'admin';
  const isTemplate = !!authUser?.currentWorkspace?.workspaces?.is_template;

  const isExampleEvaluatorsHidden = hiddenExamples.includes(
    EntityType.Evaluators,
  );

  const applyEvaluatorToForm = (evaluator: Evaluator) => {
    setEditingEvaluator(evaluator);
    setEvaluatorName(evaluator.name);
    setEvaluatorStatement(evaluator.statement);
    setSectorId(evaluator.sector_id);
  };

  // Sync URL (evaluatorId) with selection: open shared links or clear when ID missing/invalid
  useEffect(() => {
    if (loading) return;
    if (!evaluatorIdFromUrl) {
      setEditingEvaluator(null);
      setEvaluatorName('');
      setEvaluatorStatement('');
      return;
    }
    const evaluator = evaluators.find((e) => e.id === evaluatorIdFromUrl);
    if (!evaluator || evaluator.is_example) {
      navigate('/evaluator-builder', { replace: true });
      setEditingEvaluator(null);
      setEvaluatorName('');
      setEvaluatorStatement('');
      return;
    }
    if (evaluator.id !== editingEvaluator?.id) {
      if (!evaluator.is_example && isAdmin) {
        applyEvaluatorToForm(evaluator);
      } else {
        setEditingEvaluator(evaluator);
        setEvaluatorName('');
        setEvaluatorStatement('');
      }
    }
  }, [evaluatorIdFromUrl, evaluators, loading, isAdmin, navigate]);

  const handleEdit = (evaluator: Evaluator) => {
    markMilestoneComplete('evaluatorConfigured');
    if (evaluator.is_example) return;
    navigate(`/evaluator-builder/${evaluator.id}`, { replace: true });
    if (isAdmin) {
      applyEvaluatorToForm(evaluator);
    } else {
      setEditingEvaluator(evaluator);
      setEvaluatorName('');
      setEvaluatorStatement('');
    }
  };

  const handleCancelEdit = () => {
    setEditingEvaluator(null);
    setEvaluatorName('');
    setEvaluatorStatement('');
    setSectorId(null);
    navigate('/evaluator-builder', { replace: true });
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    if (!evaluatorName.trim() || !evaluatorStatement.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const evaluatorData = {
        name: evaluatorName,
        statement: evaluatorStatement,
        sector_id: isTemplate ? sectorId : null,
      };

      if (editingEvaluator) {
        await updateEvaluator(editingEvaluator.id, evaluatorData);
      } else {
        await createEvaluator(evaluatorData);
      }

      // Evaluators do not have drafts; every saved evaluator is usable in simulations.
      markMilestoneComplete('evaluatorConfigured');

      toast.success('Evaluator saved');

      handleCancelEdit();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save evaluator',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (evaluatorId: string) => {
    if (!isAdmin) return;

    if (confirm('Are you sure you want to delete this evaluator?')) {
      try {
        await deleteEvaluator(evaluatorId);
        if (editingEvaluator?.id === evaluatorId) {
          handleCancelEdit();
        }
        toast.success('Evaluator deleted');
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to delete evaluator',
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-prompt-safecomvulcan">Loading evaluators...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Evaluator Builder"
            icon={
              <ClipboardList className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />

          <div className="flex gap-6 p-6 max-lg:flex-col">
            <ExistingEvaluators
              evaluators={evaluators}
              editingEvaluator={editingEvaluator}
              isAdmin={isAdmin}
              isOnboardingActive={isOnboardingActive}
              isExampleEvaluatorsHidden={isExampleEvaluatorsHidden}
              isTemplate={isTemplate}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleHideExamples={() =>
                toggleHiddenExample(EntityType.Evaluators)
              }
              workspaceId={authUser?.currentWorkspace?.workspace_id}
            />

            {isAdmin && (
              <EvaluatorForm
                editingEvaluator={editingEvaluator}
                evaluatorName={evaluatorName}
                evaluatorStatement={evaluatorStatement}
                onNameChange={setEvaluatorName}
                onStatementChange={setEvaluatorStatement}
                isSaving={isSaving}
                onCancelEdit={handleCancelEdit}
                onSave={handleSave}
                isTemplate={isTemplate}
                sectorId={sectorId}
                onSectorChange={setSectorId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
